import React, { useEffect, useState } from "react";
import { durationLabel } from "@/lib/utils";
import { usePaymentMethodGate, PaymentMethodDialog, PaymentMethodSummary } from "@/components/PaymentMethodGate";
import { goToCheckout } from "@/lib/checkout";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, qrUrl, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PlanRecapCard } from "@/components/PlanRecapCard";
import { formatRemaining, useTicker, isExpiringSoon, isRecentlyExpired } from "@/lib/boost";
import { REGION_PLANS, eurToFcfa, regionForCountry, buildPlans, annualSecondarySavings } from "@/lib/plans";
import { readPreviewRegion } from "@/components/PreviewRegionBanner";
import { formatMoney, formatMoneyInt } from "@/lib/money";
import PageHeader from "@/components/PageHeader";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { PARTNER_CATEGORY_KEYS, partnerCategoryLabel } from "@/lib/partnerCategories";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import CoverCropper from "@/components/CoverCropper";
import { BillingInvoices } from "@/components/BillingInvoices";
import { Check, Copy, Download, Gift, Send, Upload, Trash2, QrCode as QrIcon, Sparkles, SlidersHorizontal, Bell, BadgeCheck, Eye, TrendingUp, RefreshCw, CreditCard, Link2, Globe, Receipt, Star } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const TIER_ORDER = ["free", "small", "medium", "large"];

const LANG_LOCALE = { fr: "fr-FR", en: "en-GB", es: "es-ES", de: "de-DE", it: "it-IT", pt: "pt-PT", zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", ar: "ar", he: "he-IL", ru: "ru-RU", uk: "uk-UA", pl: "pl-PL", nl: "nl-NL", tr: "tr-TR", hi: "hi-IN", id: "id-ID", th: "th-TH", vi: "vi-VN", el: "el-GR", sv: "sv-SE", da: "da-DK", fi: "fi-FI", no: "nb-NO", cs: "cs-CZ", ro: "ro-RO", hu: "hu-HU", sk: "sk-SK", bg: "bg-BG", hr: "hr-HR", ca: "ca-ES" };
function fmtDate(iso, lang = "fr") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(LANG_LOCALE[lang] || lang || "fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function Account() {
  const { partner, refresh, setPartner, logout } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [tiers, setTiers] = useState(null);
  const [cguModal, setCguModal] = useState(null);
  const [cguChecked, setCguChecked] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [boostTiers, setBoostTiers] = useState([]);
  const [boostRegion, setBoostRegion] = useState(null);
  const [boostClicks, setBoostClicks] = useState(null);
  const [referral, setReferral] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [delDetails, setDelDetails] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [phone, setPhone] = useState(partner?.phone || "");
  const [city, setCity] = useState(partner?.city || "");
  const [streetNumber, setStreetNumber] = useState(partner?.street_number || "");
  const [streetName, setStreetName] = useState(partner?.street_name || "");
  const [postalCode, setPostalCode] = useState(partner?.postal_code || "");
  const [category, setCategory] = useState(partner?.category || "");
  const [siret, setSiret] = useState(partner?.siret || "");
  const [vatNumber, setVatNumber] = useState(partner?.vat_number || "");
  const [threshold, setThreshold] = useState(Math.round((partner?.visual_automatch_threshold ?? 0.8) * 100));
  const [planRegion, setPlanRegion] = useState(readPreviewRegion() || regionForCountry(partner?.country));
  const [planCycle, setPlanCycle] = useState(() => {
    try { return localStorage.getItem("findo_plan_cycle") || "monthly"; } catch { return "monthly"; }
  });
  const recommendedTier = (() => { try { return localStorage.getItem("findo_plan_tier"); } catch { return null; } })();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [verifyingVat, setVerifyingVat] = useState(false);
  const [extRecoveryUrl, setExtRecoveryUrl] = useState(partner?.external_recovery_url || "");
  const [reminderHours, setReminderHours] = useState(partner?.external_payment_reminder_hours || 48);
  useEffect(() => {
    setExtRecoveryUrl(partner?.external_recovery_url || "");
    setReminderHours(partner?.external_payment_reminder_hours || 48);
  }, [partner?.external_recovery_url, partner?.external_payment_reminder_hours]);

  useEffect(() => {
    if (readPreviewRegion()) return;
    setPlanRegion(regionForCountry(partner?.country));
  }, [partner?.country]);

  // Fallback: if the partner has no country on file, detect region via IP.
  useEffect(() => {
    if (readPreviewRegion() || partner?.country) return;
    api("/geo/my-country", { auth: false })
      .then((r) => { if (r?.country) setPlanRegion(regionForCountry(r.country)); })
      .catch(() => {});
  }, [partner?.country]);

  // Live-apply the superadmin preview region override.
  useEffect(() => {
    const sync = () => { const p = readPreviewRegion(); if (p) setPlanRegion(p); };
    window.addEventListener("findloop:preview-region-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("findloop:preview-region-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [subReceipts, setSubReceipts] = useState(null);
  useTicker();

  useEffect(() => {
    api("/auth/me/subscription-receipts").then((r) => setSubReceipts(r.receipts || [])).catch(() => setSubReceipts([]));
  }, []);

  useEffect(() => {
    if (partner?.visual_automatch_threshold != null) setThreshold(Math.round(partner.visual_automatch_threshold * 100));
  }, [partner?.visual_automatch_threshold]);

  async function saveThreshold() {
    setBusy(true);
    try {
      const updated = await api("/auth/me/visual-threshold", { method: "POST", body: { threshold: threshold / 100 } });
      setPartner(updated);
      toast.success(t("ac.tSensitivity"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function savePaymentLinks() {
    setBusy(true);
    try {
      const updated = await api("/auth/me/payment-links", {
        method: "POST",
        body: {
          external_recovery_url: extRecoveryUrl.trim(),
          reminder_hours: Math.min(720, Math.max(1, parseInt(reminderHours, 10) || 48)),
        },
      });
      setPartner(updated);
      toast.success(t("ac.tPayLinks"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function downloadExternalCsv() {
    try {
      const res = await fetch(`${API}/declarations/external-payments/export.csv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(t("ac.tExportImpossible"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-paiements-externes.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  const emailPrefs = partner?.email_prefs || { new_declaration: true, match: true, shipment: true };
  async function toggleEmailPref(kind) {
    try {
      const updated = await api("/auth/me/email-preferences", { method: "POST", body: { [kind]: !emailPrefs[kind] } });
      setPartner(updated);
      toast.success(t("ac.tEmailPrefs"));
    } catch (e) { toast.error(e.message); }
  }

  useEffect(() => {
    api("/config/tiers", { auth: false }).then((r) => setTiers(r.tiers)).catch(() => {});
    api("/partners/me/boost-clicks").then(setBoostClicks).catch(() => {});
    api("/referrals/mine").then(setReferral).catch(() => {});
  }, []);

  useEffect(() => {
    const c = partner?.country ? `?country=${encodeURIComponent(partner.country)}` : "";
    api(`/partners/boost/tiers${c}`, { auth: false }).then((r) => { setBoostTiers(r.tiers || []); setBoostRegion(r.region || null); }).catch(() => {});
  }, [partner?.country]);

  // Paid offers require a saved card / IBAN first (resumed after the Stripe setup).
  const pmGate = usePaymentMethodGate((p) => {
    if (p.kind === "boost") doBoost(p.tier);
    if (p.kind === "subscribe") { if (p.cycle) setPlanCycle(p.cycle); openCgu(p.tier, p.region); }
  });
  const boostListing = (tier) => pmGate.require({ kind: "boost", tier }, () => doBoost(tier));

  async function doBoost(tier) {
    setBusy(true);
    try {
      const res = await api("/partners/me/boost", { method: "POST", body: { tier, origin_url: window.location.origin } });
      goToCheckout(res.url);
    } catch (e) { toast.error(e.message); setBusy(false); }
  }

  // Coming from the public pricing « Choisir » while logged in → straight to checkout (CGU consent first).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("subscribe");
    if (!wanted || !partner) return;
    const wantedCycle = params.get("cycle");
    if (wantedCycle === "annual" || wantedCycle === "monthly") setPlanCycle(wantedCycle);
    window.history.replaceState(null, "", window.location.pathname);
    if (wanted !== "free" && wanted !== partner.tier) subscribe(wanted, planRegion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner?.id]);

  function subscribe(tier, region) {
    pmGate.require({ kind: "subscribe", tier, region, cycle: planCycle }, () => openCgu(tier, region));
  }

  function openCgu(tier, region) {
    // Mandatory CGU consent before any (re)subscription (first + manual renewal).
    setCguModal({ tier, region });
    setCguChecked(false);
  }

  async function confirmSubscribe() {
    if (!cguModal || !cguChecked) return;
    const { tier, region } = cguModal;
    setBusy(true);
    try {
      const res = await api("/checkout/session", {
        method: "POST",
        body: { kind: "subscription", tier, region, billing_cycle: planCycle, origin_url: window.location.origin, cgu_accepted: true },
      });
      try { localStorage.removeItem("findo_plan_cycle"); localStorage.removeItem("findo_plan_region"); localStorage.removeItem("findo_plan_tier"); } catch { /* ignore */ }
      goToCheckout(res.url);
    } catch (e) { toast.error(e.message); setBusy(false); setCguModal(null); }
  }

  async function cancelSub() {
    setBusy(true);
    try {
      await api("/auth/me/subscription/cancel", { method: "POST" });
      toast.success(t("sub.cancelledToast"));
      setCancelModal(false);
      await refresh?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function resumeSub() {
    setBusy(true);
    try {
      await api("/auth/me/subscription/resume", { method: "POST" });
      toast.success(t("sub.resumedToast"));
      await refresh?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function downloadSubReceipt(sessionId) {
    try {
      const path = sessionId ? `/auth/me/subscription-receipt/${sessionId}.pdf` : `/auth/me/subscription-receipt.pdf`;
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(res.status === 404 ? t("ac.noSubReceipt") : t("ac.tErr"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(e.message); }
  }

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    try {
      const updated = await api("/auth/me/logo", { method: "POST", body: { image_base64: b64 } });
      setPartner(updated);
      toast.success(t("ac.tLogo"));
    } catch (err) { toast.error(err.message); }
  }
  async function removeLogo() {
    try { const u = await api("/auth/me/logo", { method: "DELETE" }); setPartner(u); toast.success(t("ac.tLogoRemoved")); }
    catch (e) { toast.error(e.message); }
  }
  function pickCover(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setCropSrc(r.result);
    r.readAsDataURL(file);
    e.target.value = "";
  }
  async function saveCover(b64) {
    try {
      const updated = await api("/auth/me/cover", { method: "POST", body: { image_base64: b64 } });
      setPartner(updated);
      toast.success(t("ac.tCover"));
    } catch (err) { toast.error(err.message || t("ac.tErr")); }
  }
  async function removeCover() {
    setPartner((p) => ({ ...p, cover_base64: null }));
    try { const u = await api("/auth/me/cover", { method: "DELETE" }); setPartner(u); toast.success(t("cover.remove")); }
    catch (e) { toast.error(e.message); refresh(); }
  }
  async function savePhone() {
    try { const u = await api("/auth/me/phone", { method: "POST", body: { phone } }); setPartner(u); toast.success(t("ac.tPhone")); }
    catch (e) { toast.error(e.message); }
  }

  async function saveCity() {
    if (!city.trim()) { toast.error(t("ac.tCityNeeded")); return; }
    try { const u = await api("/auth/me/city", { method: "POST", body: { city: city.trim() } }); setPartner(u); toast.success(t("ac.tCity")); }
    catch (e) { toast.error(e.message); }
  }

  async function saveAddressField(field, value) {
    try {
      const u = await api("/auth/me/address", { method: "POST", body: { [field]: value } });
      setPartner(u);
      toast.success(t("hc.addressSaved"));
    } catch (e) { toast.error(e.message); }
  }

  async function saveCategory(value) {
    setCategory(value);
    try { const u = await api("/auth/me/category", { method: "POST", body: { category: value } }); setPartner(u); toast.success(t("ac.tCategory")); }
    catch (e) { toast.error(e.message); }
  }

  async function saveLegal() {
    if (partner.country === "FR" && (!siret.trim() || !vatNumber.trim())) { toast.error(t("ac.tLegalNeeded")); return; }
    try { const u = await api("/auth/me/legal", { method: "POST", body: { siret: siret.trim(), vat_number: vatNumber.trim() } }); setPartner(u); toast.success(t("ac.tLegal")); }
    catch (e) { toast.error(e.message); }
  }

  async function verifyVat() {
    if (!vatNumber.trim()) return;
    setVerifyingVat(true);
    try {
      const r = await api("/auth/me/verify-vat", { method: "POST", body: { vat_number: vatNumber.trim() } });
      if (r.valid) toast.success(t("ac.vatValidFmt").replace("{name}", r.name ? ` · ${r.name}` : "").replace("{via}", r.checked_via === "vies" ? "VIES" : "format"));
      else toast.error(r.reason || t("ac.tVatInvalid"));
      try { const me = await api("/auth/me"); setPartner(me); } catch { /* ignore */ }
    } catch (e) { toast.error(e.message); } finally { setVerifyingVat(false); }
  }

  function copy(text) { navigator.clipboard.writeText(text); toast.success(t("ac.tCopied")); }

  async function confirmDeleteAccount() {
    if (!delReason) { toast.error(t("hc.pleaseProvideAReason")); return; }
    setDelBusy(true);
    try {
      await api("/auth/me/delete-account", { method: "POST", body: { reason: delReason, details: delDetails.trim() || undefined } });
      setDeleteModal(false);
      toast.success(t("hc.accountDeactivatedAReactivationEmail"));
      setTimeout(() => { logout(); navigate("/"); }, 1200);
    } catch (e) {
      toast.error(e.message || "Impossible de supprimer le compte.");
    } finally {
      setDelBusy(false);
    }
  }

  async function downloadQR() {
    const res = await fetch(qrUrl(partner.id));
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `findloop-qr-${partner.org_name}.png`;
    a.click();
  }
  function downloadPoster() {
    window.open(`${API}/partners/me/poster.pdf`, "_blank");
  }

  if (!partner) return null;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <PageHeader title={t("db.account.t")} subtitle={t("db.account.s")} testid="account-header" />

      <Tabs defaultValue="subscription">
        <TabsList className="rounded-xl">
          <TabsTrigger value="subscription" data-testid="tab-subscription">{t("ac.tabSub")}</TabsTrigger>
          <TabsTrigger value="qr" data-testid="tab-qr">{t("ac.tabQr")}</TabsTrigger>
          <TabsTrigger value="boost" data-testid="tab-boost">{t("ac.tabBoost")}</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral">{t("ac.tabReferral")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">{t("ac.tabPayments")}</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">{t("ac.tabProfile")}</TabsTrigger>
        </TabsList>

        {/* Subscription */}
        <TabsContent value="subscription" className="mt-6">
          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="plan-cycle-toggle">
              <button onClick={() => setPlanCycle("monthly")} data-testid="plan-cycle-monthly"
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${planCycle === "monthly" ? "bg-white shadow text-findo-indigo" : "text-slate-500"}`}>
                {t("pricing.monthly") === "pricing.monthly" ? "Mensuel" : t("pricing.monthly")}
              </button>
              <button onClick={() => setPlanCycle("annual")} data-testid="plan-cycle-annual"
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${planCycle === "annual" ? "bg-white shadow text-findo-indigo" : "text-slate-500"}`}>
                {t("pricing.annual") === "pricing.annual" ? "Annuel" : t("pricing.annual")}
                <span className="inline-flex items-center gap-1 rounded-full bg-findo-red text-white px-2 py-0.5 text-[11px] font-bold">
                  <Sparkles size={11} className="fill-white" /> {t("pricing.annualBadge") === "pricing.annualBadge" ? "Offre -20%" : t("pricing.annualBadge")}
                </span>
              </button>
            </div>
          </div>
          {partner.tier !== "free" && (
            <div className="mb-6 rounded-2xl border border-border bg-white/70 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="subscription-status-banner">
              <div>
                {partner.subscription_status === "cancelling" ? (
                  <p className="text-sm font-semibold text-findo-red" data-testid="subscription-ends-text">
                    {t("sub.expiresOn")} {fmtDate(partner.subscription_ends_at, lang)}
                  </p>
                ) : (
                  <p className="text-sm font-semibold text-slate-700" data-testid="subscription-renews-text">
                    {t("sub.renewsOn")} {fmtDate(partner.subscription_renews_at, lang)}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-0.5">{t("sub.noProrata")}</p>
              </div>
              {partner.subscription_status === "cancelling" ? (
                <Button size="sm" onClick={resumeSub} disabled={busy} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="resume-subscription-btn">
                  {t("sub.resume")}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setCancelModal(true)} className="rounded-full font-semibold text-findo-danger border-findo-danger/30 hover:bg-findo-danger/5" data-testid="cancel-subscription-btn">
                  {t("sub.cancel")}
                </Button>
              )}
            </div>
          )}

          {partner.subscription_status === "active" && (
            <div className="flex justify-center mb-6">
              <Button variant="outline" size="sm" onClick={() => downloadSubReceipt()} className="rounded-full font-semibold" data-testid="download-sub-receipt">
                <Download size={15} className="mr-1.5" /> {t("ac.subReceipt") === "ac.subReceipt" ? "Reçu d'abonnement (PDF)" : t("ac.subReceipt")}
              </Button>
            </div>
          )}
          <div className="flex justify-center mb-5"><PaymentMethodSummary info={pmGate.info} onChange={pmGate.change} /></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="account-pricing-grid">
            {buildPlans(planRegion, lang).map((plan) => {
              const featured = plan.featured;
              const current = partner.tier === plan.key;
              const recommended = plan.key === recommendedTier && !current;
              const isAnnual = planCycle === "annual" && !!plan.price_eur;
              const annualEur = plan.price_eur ? Math.round(plan.price_eur * 12 * 0.8) : null;
              const fullAnnualEur = plan.price_eur ? plan.price_eur * 12 : null;
              const savingsEur = plan.price_eur ? fullAnnualEur - annualEur : 0;
              const priceLabel = isAnnual ? formatMoneyInt(annualEur, lang) : plan.price;
              const subLabel = isAnnual ? (t("pricing.perYear") === "pricing.perYear" ? "/an" : t("pricing.perYear")) : plan.sub;
              return (
                <div key={plan.key} className={`rounded-[1.5rem] p-7 flex flex-col ${featured ? "bg-findo-navy text-white card-shadow" : "cream"} ${recommended ? "ring-2 ring-findo-red" : ""}`} data-testid={`tier-${plan.key}`}>
                  {featured && <span className="self-start mb-3 inline-flex items-center gap-1 text-xs font-bold badge-red px-2.5 py-1 rounded-full"><Star size={12} className="fill-white" /> {t("pricing.popular")}</span>}
                  {recommended && !featured && <span className="self-start mb-3 inline-flex items-center rounded-full bg-findo-red text-white px-2.5 py-1 text-[11px] font-bold" data-testid={`tier-recommended-${plan.key}`}>{t("hc.recommended")}</span>}
                  <h3 className={`font-display text-lg font-semibold leading-snug ${featured ? "" : "text-findo-navy"}`}>{plan.name}</h3>
                  <p className="mt-3">
                    <span className="text-3xl font-display font-semibold">{priceLabel}</span>
                    {plan.price_eur > 0 && <span className={`ml-1 text-xs font-semibold ${featured ? "text-white/70" : "text-slate-500"}`} data-testid={`plan-ht-${plan.key}`}>{t("price.ht")}</span>}
                    {subLabel && <span className={featured ? "text-white/60 text-sm" : "text-slate-400 text-sm"}>{subLabel}</span>}
                  </p>
                  {isAnnual && (
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid={`plan-annual-savings-${plan.key}`}>
                      <span className={`line-through ${featured ? "text-white/50" : "text-slate-400"}`}>{formatMoneyInt(fullAnnualEur, lang)}/an</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-findo-red/10 text-findo-red px-2 py-0.5 font-bold">−20%</span>
                      <span className={`font-bold ${featured ? "text-white" : "text-findo-red"}`}>
                        {t("pricing.saveLabel") === "pricing.saveLabel" ? "Vous économisez" : t("pricing.saveLabel")} {formatMoneyInt(savingsEur, lang)}/an
                      </span>
                    </p>
                  )}
                  {planRegion === "afrique" && !plan.quote && plan.key !== "free" && (
                    <p className={`text-xs mt-0.5 ${featured ? "text-white/60" : "text-slate-400"}`}>≈ {eurToFcfa(isAnnual ? annualEur : plan.price_eur, lang)} FCFA {t("price.ht")}{subLabel}</p>
                  )}
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {plan.features.map((f) => {
                      const isDiscount = /-?\s*20\s*%/.test(f);
                      const label = isDiscount ? f.replace(/\s*\(?-?\s*20\s*%\)?/g, "").trim() : f;
                      return (
                        <li key={f} className={`flex items-start gap-2 text-sm ${featured ? "text-white/85" : "text-slate-600"}`}>
                          <Check size={16} className={`${featured ? "text-white" : "text-findo-red"} mt-0.5 shrink-0`} />
                          <span className="flex flex-wrap items-center gap-1.5">
                            {label}
                            {isDiscount && (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="discount-badge cursor-help" data-testid={`account-discount-badge-${plan.key}`}>
                                      <Sparkles size={10} className="fill-white" /> −20%
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[220px] text-center" data-testid={`account-savings-tip-${plan.key}`}>
                                    {t("pricing.saveLabel")} <b>{formatMoneyInt(annualSecondarySavings(planRegion, plan.key), lang)}</b>{t("pricing.savePerYear")}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {current ? (
                    <div className="mt-auto pt-6">
                      <span className={`block text-center text-sm font-bold rounded-full py-2.5 ${featured ? "bg-white/15 text-white" : "bg-findo-indigo/10 text-findo-indigo"}`}>{t("ac.currentPlan")}</span>
                    </div>
                  ) : plan.quote ? (
                    <div className="mt-auto pt-6">
                      <Button onClick={() => setQuoteOpen(true)} className="w-full rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 text-white" data-testid={`subscribe-${plan.key}`}>
                        {t("ac.requestQuote")}
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-auto pt-6">
                      <Button onClick={() => subscribe(plan.key, planRegion)} disabled={busy || plan.key === "free"} className={`w-full rounded-full font-semibold ${featured ? "bg-white text-findo-navy hover:bg-white/90" : "bg-findo-navy hover:bg-findo-navy/90 text-white"}`} data-testid={`subscribe-${plan.key}`}>
                        {plan.key === "free" ? t("ac.free") : t("ac.choose")}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <AccountQuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} partner={partner} region={planRegion} />
        </TabsContent>

        {/* QR */}
        <TabsContent value="qr" className="mt-6">
          <div className="bg-white rounded-2xl border border-border p-8 card-shadow text-center max-w-md mx-auto">
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-findo-indigo mb-4"><QrIcon size={16} /> {t("ac.qrTitle")}</span>
            <div className="mx-auto w-56 h-56 rounded-2xl border border-border p-3 bg-white">
              <img src={qrUrl(partner.id)} alt={t("ac.legalPartnerQr")} className="w-full h-full" data-testid="account-qr" />
            </div>
            <p className="mt-4 text-sm text-slate-500">{t("ac.qrHint")}</p>
            <div className="mt-5 flex gap-3 justify-center">
              <Button onClick={downloadQR} variant="outline" className="rounded-xl" data-testid="download-qr"><Download size={16} className="mr-1.5" /> {t("ac.qrPng")}</Button>
              <Button onClick={downloadPoster} className="rounded-xl" data-testid="download-poster"><Download size={16} className="mr-1.5" /> {t("ac.posterA4")}</Button>
            </div>
            <button onClick={() => copy(`${window.location.origin}/declare/${partner.id}`)} className="mt-4 text-xs text-slate-400 hover:text-findo-indigo flex items-center gap-1 mx-auto">
              <Copy size={12} /> {t("ac.copyDeclLink")}
            </button>
          </div>
        </TabsContent>

        {/* Boost / Visibility */}
        <TabsContent value="boost" className="mt-6">
          <div className="bg-white rounded-2xl border border-border p-8 card-shadow max-w-3xl mx-auto">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-xl bg-findo-red grid place-items-center text-white"><Sparkles size={22} /></span>
              <div>
                <h3 className="font-display font-bold text-slate-900">{t("ac.boostTitle")}</h3>
                <p className="text-sm text-slate-500">{t("ac.boostSub")}</p>
              </div>
            </div>

            {isBoosted(partner) && (
              <div className="mt-5 rounded-xl bg-findo-red/5 border border-findo-red/30 px-4 py-3 flex items-center gap-2 text-sm font-semibold text-findo-red" data-testid="partner-boost-active">
                <Sparkles size={16} /> {t("ac.boostActive")} {formatRemaining(partner.boost_until, t)} {t("bo.remaining").toLowerCase()}
              </div>
            )}

            {isRecentlyExpired(partner.boost_until) && (
              <div className="mt-5 rounded-2xl border-2 border-findo-red bg-gradient-to-r from-findo-red/10 to-findo-red/5 p-5" data-testid="partner-boost-winback">
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-xl bg-findo-red grid place-items-center text-white shrink-0 font-extrabold text-sm">-20%</span>
                  <div className="min-w-0">
                    <h4 className="font-display font-bold text-findo-navy">{t("ac.winbackTitle")}</h4>
                    <p className="text-sm text-slate-600 mt-0.5">
                      {t("ac.winbackDesc1")} <b className="text-findo-red">{boostClicks?.total || 0} {t("ac.views")}</b>. {t("ac.winbackDesc2")}
                    </p>
                    <Button onClick={() => boostListing(partner.boost_tier || boostTiers?.[0]?.key)} disabled={busy}
                      className="mt-3 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="partner-boost-winback-btn">
                      <RefreshCw size={15} className="mr-1.5" /> {t("ac.winbackBtn")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {isExpiringSoon(partner.boost_until) && (
              <div className="mt-5 rounded-2xl border-2 border-findo-red bg-findo-red/5 p-5" data-testid="partner-boost-renew">
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-xl bg-findo-red grid place-items-center text-white shrink-0"><RefreshCw size={18} /></span>
                  <div className="min-w-0">
                    <h4 className="font-display font-bold text-findo-navy">{t("ac.expireSoon")} {formatRemaining(partner.boost_until, t)}</h4>
                    <p className="text-sm text-slate-600 mt-0.5">
                      {t("ac.renewDesc1")} <b className="text-findo-red">{boostClicks?.total || 0} {t("ac.views")}</b> {t("ac.onYourListing")}{boostClicks?.this_week ? <>{t("ac.thisWeekOf").replace("{n}", boostClicks.this_week)}</> : null}. {t("ac.renewDesc2")}
                    </p>
                    <Button onClick={() => boostListing(partner.boost_tier || boostTiers?.[0]?.key)} disabled={busy}
                      className="mt-3 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="partner-boost-renew-btn">
                      <RefreshCw size={15} className="mr-1.5" /> {t("ac.renewNow")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {boostClicks && (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="partner-boost-views">
                <div className="rounded-xl border border-border bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("ac.viewsTotal")}</p>
                  <p className="font-display text-3xl font-extrabold text-findo-navy mt-1 flex items-center gap-1.5" data-testid="boost-views-total">
                    <Eye size={20} className="text-findo-red" /> {boostClicks.total || 0}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("ac.thisWeek")}</p>
                  <p className="font-display text-3xl font-extrabold text-findo-navy mt-1" data-testid="boost-views-week">{boostClicks.this_week || 0}</p>
                </div>
                <div className="rounded-xl border border-border bg-slate-50 p-4 col-span-2 sm:col-span-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("ac.trend")}</p>
                  <p className={`font-display text-2xl font-extrabold mt-1 flex items-center gap-1 ${boostClicks.trend === "up" ? "text-emerald-600" : boostClicks.trend === "down" ? "text-findo-red" : "text-slate-500"}`} data-testid="boost-views-trend">
                    <TrendingUp size={18} className={boostClicks.trend === "down" ? "rotate-180" : ""} /> {boostClicks.delta > 0 ? "+" : ""}{boostClicks.delta || 0}
                  </p>
                </div>
              </div>
            )}

            {boostRegion === "afrique" && (
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1" data-testid="partner-boost-africa-badge">
                <Globe size={13} /> {t("bo.africaPrice")}
              </div>
            )}
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {boostTiers.map((tier) => (
                <div key={tier.key} className="rounded-2xl border border-border p-5 bg-white flex flex-col text-center" data-testid={`partner-boost-tier-${tier.key}`}>
                  <p className="text-sm text-slate-500">{tier.hours ? durationLabel(tier.hours, lang) : (lang !== "fr" && tier.label_en ? tier.label_en : tier.label_fr)}</p>
                  <p className="font-display text-3xl font-extrabold text-findo-navy mt-1">{formatMoney(tier.price_eur, lang)} <span className="text-xs font-semibold text-slate-500">{t("price.ht")}</span></p>
                  <Button onClick={() => boostListing(tier.key)} disabled={busy} className="mt-4 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid={`partner-boost-buy-${tier.key}`}>
                    {isBoosted(partner) ? t("bo.renew") : t("ad.boost")}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Referral */}
        <TabsContent value="referral" className="mt-6">
          <div className="bg-white rounded-2xl border border-border p-8 card-shadow max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-xl findo-gradient grid place-items-center text-white"><Gift size={24} /></span>
              <div>
                <h3 className="font-display font-bold text-slate-900">{t("ac.refTitle")}</h3>
                <p className="text-sm text-slate-500">{t("ac.refSub")}</p>
              </div>
            </div>
            {referral && (
              <>
                <div className="mt-6 flex gap-3">
                  <div className="flex-1 rounded-xl border border-border bg-slate-50 px-4 py-3 font-mono font-bold text-findo-indigo tracking-wider" data-testid="referral-code">{referral.referral_code}</div>
                  <Button variant="outline" onClick={() => copy(referral.referral_url)} className="rounded-xl"><Copy size={16} className="mr-1.5" /> {t("ac.copyLink")}</Button>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <Stat n={referral.total_invited} l={t("ac.invited")} />
                  <Stat n={referral.total_converted} l={t("ac.converted")} />
                  <Stat n={referral.free_months_credits} l={t("ac.freeMonths")} />
                </div>
              </>
            )}
          </div>
        </TabsContent>

        {/* Profile */}
        {/* External payment links (hybrid payment system) */}
        <TabsContent value="payments" className="mt-6">
          <div className="max-w-3xl mx-auto mb-6">
            <BillingInvoices partner={partner} />
          </div>
          <div className="bg-white rounded-2xl border border-border p-8 card-shadow max-w-3xl mx-auto" data-testid="payments-panel">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-xl bg-findo-indigo grid place-items-center text-white"><CreditCard size={22} /></span>
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900">{t("ac.payTitle")}</h3>
                <p className="text-sm text-slate-500">{t("ac.paySub")}</p>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
              <p className="font-semibold">{t("ac.howItWorks")}</p>
              <ul className="mt-1.5 space-y-1 list-disc pl-5">
                <li>{t("ac.pay1")}</li>
                <li>{t("ac.pay2")}</li>
                <li>{t("ac.pay3")}</li>
              </ul>
            </div>

            <div className="mt-6 space-y-5">
              <div className="rounded-xl bg-findo-indigo/5 border border-findo-indigo/20 p-4 flex items-start gap-3" data-testid="payments-moved-notice">
                <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo shrink-0"><Link2 size={18} /></span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{t("hc.paymentLinksAreNowConfigured")}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{t("hc.eachSiteManagesItsOwn")} <b>{t("hc.settings")}</b> {t("hc.toConfigureTheLinksAnd")}</p>
                  <Button onClick={() => navigate("/b2b/settings")} className="mt-3 rounded-xl font-semibold" data-testid="go-to-settings">
                    {t("hc.openSettings")}
                  </Button>
                </div>
              </div>

              <div className="mt-2 pt-4 border-t border-border flex items-center justify-between gap-3">
                <p className="text-sm text-slate-500">{t("ac.exportHint")}</p>
                <Button onClick={downloadExternalCsv} variant="outline" className="rounded-xl font-semibold" data-testid="export-external-payments-csv">
                  <Download size={16} className="mr-1.5" /> {t("ac.exportCsv")}
                </Button>
              </div>
            </div>
          </div>

          {/* Subscription receipts history */}
          <div className="bg-white rounded-2xl border border-border p-8 card-shadow max-w-3xl mx-auto mt-6" data-testid="sub-receipts-panel">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-xl bg-findo-navy grid place-items-center text-white"><Receipt size={22} /></span>
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900">{t("ac.subReceiptsTitle") === "ac.subReceiptsTitle" ? "Reçus d'abonnement" : t("ac.subReceiptsTitle")}</h3>
                <p className="text-sm text-slate-500">{t("ac.subReceiptsSub") === "ac.subReceiptsSub" ? "Téléchargez le reçu PDF de chaque paiement d'abonnement." : t("ac.subReceiptsSub")}</p>
              </div>
            </div>
            {subReceipts === null ? (
              <div className="mt-5 space-y-2">{[0, 1].map((i) => <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />)}</div>
            ) : subReceipts.length === 0 ? (
              <p className="mt-5 text-sm text-slate-400" data-testid="sub-receipts-empty">{t("ac.noSubReceipt") === "ac.noSubReceipt" ? "Aucun paiement d'abonnement réglé pour le moment." : t("ac.noSubReceipt")}</p>
            ) : (
              <div className="mt-5 divide-y divide-border" data-testid="sub-receipts-list">
                {subReceipts.map((r) => (
                  <div key={r.session_id} className="flex items-center justify-between gap-3 py-3" data-testid={`sub-receipt-row-${r.session_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {r.tier}
                        <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${r.billing_cycle === "annual" ? "bg-findo-red/10 text-findo-red" : "bg-slate-100 text-slate-500"}`}>
                          {r.billing_cycle === "annual" ? t("hc.annual20") : t("hc.monthly")}
                        </span>
                      </p>
                      <p className="text-xs text-slate-400">
                        {r.number} · {r.paid_at ? fmtDate(r.paid_at, lang) : ""} · <b>{Number(r.amount_eur).toFixed(2)} €</b>
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => downloadSubReceipt(r.session_id)} className="rounded-full font-semibold shrink-0" data-testid={`sub-receipt-dl-${r.session_id}`}>
                      <Download size={15} className="mr-1.5" /> PDF
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="profile" className="mt-6">
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow mb-6">
            <h3 className="font-display font-bold text-slate-900 mb-1">{t("cover.title")}</h3>
            <p className="text-sm text-slate-500 mb-4">{t("cover.helper")}</p>
            <div className="relative rounded-2xl overflow-hidden border border-border h-48 bg-slate-50 photo-scrim">
              {partner.cover_base64 ? (
                <img src={partner.cover_base64} alt={t("hc.cover")} className="w-full h-full object-cover" data-testid="cover-preview" />
              ) : (
                <div className="w-full h-full grid place-items-center text-slate-300 text-sm">{t("cover.none")}</div>
              )}
              <div className="absolute bottom-3 right-3 flex gap-2">
                <label>
                  <input type="file" accept="image/*" className="hidden" onChange={pickCover} data-testid="cover-input" />
                  <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-full bg-white/95 text-findo-navy px-3.5 py-2 text-sm font-semibold hover:bg-white shadow"><Upload size={15} /> {partner.cover_base64 ? t("cover.change") : t("cover.add")}</span>
                </label>
                {partner.cover_base64 && (
                  <button onClick={removeCover} data-testid="cover-remove" className="inline-flex items-center gap-1.5 rounded-full bg-white/95 text-red-600 px-3.5 py-2 text-sm font-semibold hover:bg-white shadow"><Trash2 size={15} /> {t("cover.remove")}</button>
                )}
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-border p-6 card-shadow">
              <h3 className="font-display font-bold text-slate-900 mb-4">{t("ac.logoTitle")}</h3>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-2xl border border-border overflow-hidden bg-slate-50 grid place-items-center">
                  {partner.logo_base64 ? <img src={partner.logo_base64} alt="logo" className="w-full h-full object-cover" /> : <span className="text-slate-300 text-xs">{t("ac.none")}</span>}
                </div>
                <div className="space-y-2">
                  <label>
                    <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-input" />
                    <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-xl bg-findo-indigo text-white px-3 py-2 text-sm font-semibold hover:opacity-90"><Upload size={15} /> {t("ac.upload")}</span>
                  </label>
                  {partner.logo_base64 && <Button variant="ghost" size="sm" onClick={removeLogo} className="text-red-600 rounded-xl"><Trash2 size={15} className="mr-1" /> {t("cover.remove")}</Button>}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6 card-shadow">
              <h3 className="font-display font-bold text-slate-900 mb-4">{t("ac.contact")}</h3>
              <Label className="text-xs text-slate-400">{t("a.email")}</Label>
              <p className="font-medium text-slate-800 mb-4">{partner.email}</p>
              <Label htmlFor="phone">{t("ac.phone")}</Label>
              <div className="flex gap-2 mt-1.5">
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" className="h-11 rounded-xl" data-testid="phone-input" />
                <Button onClick={savePhone} className="rounded-xl">{t("c.save")}</Button>
              </div>
              <Label htmlFor="city" className="mt-4 block">{t("ac.cityLabel")}</Label>
              <p className="text-xs text-slate-400 mb-1.5">{t("ac.cityHint")}</p>
              <div className="flex gap-2">
                <div className="flex-1"><CityAutocomplete value={city} onChange={setCity} country={partner.country} placeholder={t("ac.cityPh")} className="h-11 rounded-xl" testid="city-input" /></div>
                <Button onClick={saveCity} className="rounded-xl" data-testid="city-save">{t("c.save")}</Button>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2" data-testid="address-fields">
                <div>
                  <Label className="text-xs text-slate-400">{t("hc.streetNo")} <span className="text-slate-300">{t("hc.optional2")}</span></Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} placeholder="12" className="h-11 rounded-xl" data-testid="street-number-input" />
                    <Button variant="outline" onClick={() => saveAddressField("street_number", streetNumber.trim())} className="rounded-xl px-3" data-testid="street-number-save">OK</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">{t("hc.streetName")} <span className="text-slate-300">{t("hc.optional2")}</span></Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input value={streetName} onChange={(e) => setStreetName(e.target.value)} placeholder={t("hc.mainStreet")} className="h-11 rounded-xl" data-testid="street-name-input" />
                    <Button variant="outline" onClick={() => saveAddressField("street_name", streetName.trim())} className="rounded-xl px-3" data-testid="street-name-save">OK</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">{t("pay.postal")} <span className="text-slate-300">{t("hc.optional2")}</span></Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="75001" className="h-11 rounded-xl" data-testid="postal-code-input" />
                    <Button variant="outline" onClick={() => saveAddressField("postal_code", postalCode.trim())} className="rounded-xl px-3" data-testid="postal-code-save">OK</Button>
                  </div>
                </div>
              </div>
              <Label htmlFor="category" className="mt-4 block">{t("a.category")}</Label>
              <p className="text-xs text-slate-400 mb-1.5">{t("ac.categoryHint")}</p>
              <Select value={category} onValueChange={saveCategory}>
                <SelectTrigger className="h-11 rounded-full" data-testid="category-input">
                  <SelectValue placeholder={t("a.categoryPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {PARTNER_CATEGORY_KEYS.map((k) => (
                    <SelectItem key={k} value={k} data-testid={`category-opt-${k}`}>{partnerCategoryLabel(t, k)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label htmlFor="siret" className="mt-4 block">{t("ac.legalTitle")}</Label>
              <p className="text-xs text-slate-400 mb-1.5">
                {partner.country === "FR"
                  ? t("ac.legalFR")
                  : t("ac.legalOther")}
              </p>
              {partner.country === "FR" && (!partner.siret || !partner.vat_number) && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 mb-2" data-testid="legal-missing-banner">
                  {t("ac.legalMissing")}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="SIRET" className="h-11 rounded-xl" data-testid="siret-input" />
                <Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} placeholder="TVA (FR...)" className="h-11 rounded-xl uppercase" data-testid="vat-input" />
              </div>
              {partner.vat_validated && (
                <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1 mt-1" data-testid="vat-validated-badge">
                  <BadgeCheck size={13} /> {t("ac.vatVerified")}{partner.vat_validated_name ? ` · ${partner.vat_validated_name}` : ""}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <Button onClick={saveLegal} className="rounded-xl" data-testid="legal-save">{t("ac.saveLegal")}</Button>
                <Button onClick={verifyVat} disabled={verifyingVat || !vatNumber.trim()} variant="outline" className="rounded-xl" data-testid="vat-verify">
                  {verifyingVat ? t("ac.verifying") : t("ac.verifyVat")}
                </Button>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow mt-6" data-testid="visual-threshold-card">
            <div className="flex items-center gap-2 mb-1">
              <SlidersHorizontal size={18} className="text-findo-indigo" />
              <h3 className="font-display font-bold text-slate-900">{t("ac.thresholdTitle")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("ac.thresholdDesc")}
            </p>
            <div className="flex items-center gap-4">
              <span className="text-xs text-slate-400 w-20">{t("ac.permissive")}</span>
              <input type="range" min="50" max="99" value={threshold} onChange={(e) => setThreshold(+e.target.value)}
                className="flex-1 accent-findo-indigo cursor-pointer" data-testid="threshold-slider" />
              <span className="text-xs text-slate-400 w-16 text-right">{t("ac.strict")}</span>
              <span className="min-w-14 text-center text-lg font-display font-extrabold text-findo-indigo" data-testid="threshold-value">{threshold}%</span>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveThreshold} disabled={busy} className="rounded-xl" data-testid="threshold-save">{t("c.save")}</Button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border p-6 card-shadow mt-6" data-testid="email-prefs-card">
            <div className="flex items-center gap-2 mb-1">
              <Bell size={18} className="text-findo-indigo" />
              <h3 className="font-display font-bold text-slate-900">{t("ac.emailTitle")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("ac.emailDesc")}
            </p>
            <div className="divide-y divide-border">
              {[
                ["new_declaration", t("ac.emailNewDecl"), t("ac.emailNewDeclDesc")],
                ["match", t("ac.emailMatch"), t("ac.emailMatchDesc")],
                ["shipment", t("ac.emailShip"), t("ac.emailShipDesc")],
                ["cross_match",
                 t("ac.emailCrossMatch") === "ac.emailCrossMatch" ? "Correspondances croisées B2C" : t("ac.emailCrossMatch"),
                 t("ac.emailCrossMatchDesc") === "ac.emailCrossMatchDesc" ? "Être averti quand une annonce publique d'objet perdu correspond à un objet déposé chez vous." : t("ac.emailCrossMatchDesc")],
                ["cross_match_reject",
                 t("ac.emailCrossReject") === "ac.emailCrossReject" ? "Rejets de correspondance publique" : t("ac.emailCrossReject"),
                 t("ac.emailCrossRejectDesc") === "ac.emailCrossRejectDesc" ? "Recevoir un email quand un client indique qu'une correspondance publique proposée n'est pas son objet." : t("ac.emailCrossRejectDesc")],
                ["login_notification",
                 t("ac.emailLoginNotif") === "ac.emailLoginNotif" ? "Notification de connexion" : t("ac.emailLoginNotif"),
                 t("ac.emailLoginNotifDesc") === "ac.emailLoginNotifDesc" ? "Recevoir un email à chaque connexion à votre compte." : t("ac.emailLoginNotifDesc")],
              ].map(([kind, title, desc]) => (
                <div key={kind} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{title}</p>
                    <p className="text-xs text-slate-400">{desc}</p>
                  </div>
                  <Switch checked={kind === "login_notification" ? emailPrefs[kind] === true : emailPrefs[kind] !== false} onCheckedChange={() => toggleEmailPref(kind)} data-testid={`email-pref-${kind}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-red-200 p-6 card-shadow mt-6" data-testid="danger-zone-card">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 size={18} className="text-red-600" />
              <h3 className="font-display font-bold text-red-700">{t("hc.dangerZone")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("hc.deletingDeactivatesYourAccountLogin")}
            </p>
            <Button variant="outline" onClick={() => setDeleteModal(true)}
              className="rounded-xl text-red-600 border-red-300 hover:bg-red-50 font-semibold" data-testid="delete-account-btn">
              <Trash2 size={15} className="mr-1.5" /> {t("hc.deleteMyAccount")}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <PaymentMethodDialog {...pmGate.dialogProps} />
      <CoverCropper src={cropSrc} open={!!cropSrc} onClose={() => setCropSrc(null)} onCropped={saveCover} />

      <Dialog open={deleteModal} onOpenChange={(o) => { if (!o && !delBusy) setDeleteModal(false); }}>
        <DialogContent data-testid="delete-account-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 size={18} /> {t("hc.deleteMyAccount")}</DialogTitle>
            <DialogDescription>
              {t("hc.helpUsImproveWhyAre")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">{t("hc.mainReason")}</Label>
              <Select value={delReason} onValueChange={setDelReason}>
                <SelectTrigger className="h-11 rounded-full mt-1.5" data-testid="delete-reason-select">
                  <SelectValue placeholder={t("hc.chooseAReason")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price" data-testid="delete-reason-price">{t("hc.tooExpensiveBudget")}</SelectItem>
                  <SelectItem value="missing_features" data-testid="delete-reason-features">{t("hc.missingFeatures")}</SelectItem>
                  <SelectItem value="not_using" data-testid="delete-reason-notusing">{t("hc.iDonTUseIt")}</SelectItem>
                  <SelectItem value="other" data-testid="delete-reason-other">{t("hc.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">{t("hc.anyCommentsOptional")}</Label>
              <Textarea value={delDetails} onChange={(e) => setDelDetails(e.target.value)} maxLength={1000}
                placeholder={t("hc.tellUsMore")} className="rounded-xl mt-1.5" rows={3} data-testid="delete-details-input" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDeleteModal(false)} disabled={delBusy} className="rounded-xl" data-testid="delete-cancel-btn">{t("hc.cancel")}</Button>
              <Button onClick={confirmDeleteAccount} disabled={delBusy || !delReason}
                className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="delete-confirm-btn">
                {delBusy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("hc.deleting")}</span> : t("hc.confirmDeletion")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mandatory CGU consent before any (re)subscription */}
      <Dialog open={!!cguModal} onOpenChange={(o) => { if (!o) setCguModal(null); }}>
        <DialogContent data-testid="cgu-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("sub.cguTitle")}</DialogTitle>
            <DialogDescription>{t("sub.cguDesc")}</DialogDescription>
          </DialogHeader>
          {cguModal && (() => {
            const plan = buildPlans(cguModal.region, lang).find((p) => p.key === cguModal.tier);
            return <PlanRecapCard name={plan?.name || cguModal.tier} price={plan?.price_eur || 0} annual={planCycle === "annual"} />;
          })()}
          <label className="flex items-start gap-3 rounded-xl border border-border px-3 py-3 cursor-pointer" data-testid="cgu-checkbox-label">
            <input type="checkbox" checked={cguChecked} onChange={(e) => setCguChecked(e.target.checked)} className="mt-0.5 accent-findo-indigo h-4 w-4" data-testid="cgu-checkbox" />
            <span className="text-sm text-slate-700">
              {t("sub.cguPre")} <a href="/cgu" target="_blank" rel="noopener noreferrer" className="text-findo-indigo font-semibold underline" data-testid="cgu-link">{t("sub.cguLink")}</a> {t("sub.cguAnd")} <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-findo-indigo font-semibold underline" data-testid="privacy-link">{t("sub.privacyLink")}</a> {t("sub.cguSuffix")}
            </span>
          </label>
          <Button onClick={confirmSubscribe} disabled={!cguChecked || busy} className="w-full rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="cgu-confirm-pay-btn">
            {busy ? <FindoSpinner size={16} /> : t("sub.cguPay")}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Cancellation confirmation */}
      <Dialog open={cancelModal} onOpenChange={setCancelModal}>
        <DialogContent data-testid="cancel-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("sub.cancelTitle")}</DialogTitle>
            <DialogDescription data-testid="cancel-modal-desc">
              {t("sub.cancelConfirm1")} {fmtDate(partner.subscription_renews_at || partner.subscription_ends_at, lang)}. {t("sub.cancelConfirm2")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setCancelModal(false)} className="flex-1 rounded-xl font-semibold" data-testid="cancel-modal-abort">
              {t("sub.cancelAbort")}
            </Button>
            <Button onClick={cancelSub} disabled={busy} className="flex-1 rounded-xl font-semibold bg-findo-danger hover:bg-findo-danger/90" data-testid="cancel-modal-confirm">
              {busy ? <FindoSpinner size={16} /> : t("sub.cancelConfirmBtn")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function isBoosted(p) {
  return p?.boost_until && new Date(p.boost_until).getTime() > Date.now();
}

function Stat({ n, l }) {
  return <div className="rounded-xl bg-slate-50 py-3"><p className="text-2xl font-display font-extrabold text-slate-900">{n ?? 0}</p><p className="text-xs text-slate-500">{l}</p></div>;
}

function AccountQuoteDialog({ open, onClose, partner, region }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", organization: "", sites: "", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: partner?.contact_name || partner?.org_name || "", email: partner?.email || "",
      organization: partner?.org_name || "", sites: "", message: "" });
  }, [open, partner]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/public/quote-request", {
        method: "POST", auth: false,
        body: { ...form, sites: form.sites ? Number(form.sites) : null, region, segment: "Grands comptes" },
      });
      toast.success(t("ac.quoteSent"));
      onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="account-quote-dialog">
        <DialogHeader>
          <DialogTitle icon={Send}>{t("ac.quoteTitle")}</DialogTitle>
          <DialogDescription>{t("ac.quoteDesc")}{region === "afrique" ? t("ac.africa") : t("ac.quoteEurope")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div><Label>{t("ac.qName")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="account-quote-name" /></div>
          <div><Label>{t("a.email")}</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="account-quote-email" /></div>
          <div><Label>{t("ac.qOrg")}</Label><Input required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="account-quote-org" /></div>
          <div><Label>{t("ac.qSites")}</Label><Input type="number" min="1" value={form.sites} onChange={(e) => setForm({ ...form, sites: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="account-quote-sites" /></div>
          <div><Label>{t("ac.qMessage")}</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" rows={3} data-testid="account-quote-message" /></div>
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="account-quote-submit">
            <Send size={16} className="mr-1.5" /> {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("ac.sending")}</span> : t("ac.sendRequest")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
